<template>
  <div id="side-nav" class="">
    <div data-cy="sideNav" class="hamburger-menu d-lg-none" @click="toggleNav()">
      <span></span>
      <span></span>
      <span></span>
    </div>

    <div :id="isQualifiedBorrower ? 'borrower-qualified' : 'borrower-dnq'" class="d-lg-block nav-wrap me-2 m-lg-0" :class="{'d-block': sidenavToggle, 'd-none': !sidenavToggle, 'mobile-menu-pointer': sidenavToggle}">
      <ul>
        <li v-for="link in getNavLinks"
          :key="kebabString(link.title)"
          :class="{ 'd-none': link.isHidden && !checkCurrentPath(link.url) }"
          @mouseover='linkHover=link.title'
          @mouseleave='linkHover=""'
        >
          <a v-if="signOutLink(link.title)" data-cy="logout" class="m-4 d-block align-middle" :class="{disabled: zipNeeded || loggingOut, 'd-lg-none': checkMobileLinks(link.title)}" @click.prevent.once="logout()" id="sign-out">
            <span class="me-2 d-inline-block text-center"><client-only><i class="far fa-fw" :class="link.faIcon"></i></client-only></span>
            {{link.title}}
          </a>
          <a v-if="switchBusinessModal(link.title)" data-cy="switch-business" class="m-4 d-block align-middle" :class="{disabled: zipNeeded || loggingOut, 'd-lg-none': checkMobileLinks(link.title)}" @click="businessSwitchToggle(true)" id="switch-business">
            <span class="me-2 d-inline-block text-center"><client-only><i class="far fa-fw" :class="link.faIcon"></i></client-only></span>
            {{link.title}}
          </a>
          <router-link
            v-if="neitherSignOutOrModal(link.title)"
            :to="!shouldAppendExperimentOfferUrl(link.title) ? link.url : link.url + '-2'"
            class="m-4 d-block align-middle"
            :class="{
              'current': checkCurrentPath(link.url), disabled: zipNeeded || loggingOut,
              'd-lg-none': checkMobileLinks(link.title)
            }"
           :data-cy="'side-nav-' + kebabString(link.title)"
          >
            <span v-if="link.imgIcon">
              <NuxtImg class="align-middle mb-1 me-2" v-if="((linkHover === link.title) || checkCurrentPath(link.url)) && !!link.iconActive" :src="formatImagePath(link.iconActive)" />
              <NuxtImg class="align-middle mb-1 me-2" v-else :src="formatImagePath(link.imgIcon)" />
            </span>
            <span class="me-1 d-inline-block text-center" v-else><i class="fal fa-fw" :class="link.faIcon"></i></span>
            {{ link.title }}
            <ActionItemsBadge v-if="showActionItems && dashboardLink(link.title)" class="ms-1"/>
            <span v-if="link.postText" style="margin-left: 5px; background-color: #FFDD00; font-weight: 400; font-size: .8em; padding: 0px 7px; letter-spacing: 1px;">{{link.postText}}</span>
            <ActionIndicator v-if="getActionsNeeded[link.title] && getActionsNeeded[link.title].count" :actionCount="getActionsNeeded[link.title].count" />
          </router-link>
        </li>
      </ul>
      <FastTrackCTA view="desktop" :forceOpen="openFastTrack" :smallMarginBottom="true" />
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia'
import { useUserStore } from '~/store/user'

import ActionIndicator from "~/components/portal/ActionIndicator.vue"
import ActionItemsBadge from '~/components/portal/ActionItemsBadge'
import FastTrackCTA from '~/components/embedded/FastTrackCTA.vue'
import { PORTAL_NAV_TITLES, PORTAL_NAV_URLS } from "~/libs/portal-constants"
import { usePortalStore } from '~/store/portal'
import { useBorrowerStore } from '~/store/borrower'
import { useExperienceStore } from '~/store/experience'
import { useProgressCardStore } from '~/store/progress-card'
import { useBusinessSwitchStore } from '~/store/business-switch'
import { useOwnershipStore } from '~/store/ownership'
import { useRootStore } from '~/store/root'
import formatImagePath from '~/libs/image-format'

export default {
  name: 'PortalSideNav',
  props: {
    zipNeeded: {
      type: Boolean,
      default: false,
    }
  },
  async setup () {
    const { data } = await useLazyAsyncData(async () => {
      const experimentIds = await useBorrowerStore().getBorrowerExperiments();
      return {
        experimentIds,
      }
    })

    return {
      experimentIds: data.experimentIds,
      formatImagePath
    }
  },
  data () {
    return {
      sidenavToggle: false,
      linkHover: '',
      mobileOnlyLinks: [PORTAL_NAV_TITLES.SIGN_OUT, PORTAL_NAV_TITLES.SWITCH_BUSINESS],
      loggingOut: false
    }
  },
  components: {
    ActionIndicator,
    ActionItemsBadge,
    FastTrackCTA
  },
  computed: {
    ...mapState(useUserStore, ['hasMultipleBorrowers']),
    ...mapState(usePortalStore, ['isQualifiedBorrower', 'getNavLinks', 'getActionsNeeded', 'getMissingActionItemsCount']),
    ...mapState(useProgressCardStore, ['fundingSteps']),
    ...mapState(useOwnershipStore, ['isSelfServeLenderUser']),
    ...mapState(useExperienceStore, ['isDNQ']),
    ...mapState(useBorrowerStore, ['borrower']),
    showActionItems() {
      return !this.isDNQ && this.fundingSteps != 'APP_INCOMPLETE'
    },
    openFastTrack() {
      return (this.borrower !== null && this.$route.query.openFastTrack) ?? false
    }
  },
  watch: {
    '$route.path' () {
      this.sidenavToggle = false
    }
  },
  methods: {
    shouldAppendExperimentOfferUrl(title) {
      if (title !== 'Financing Options' || !this.experimentIds) {
        return false;
      }
      return this.experimentIds.includes(30) || this.isSelfServeLenderUser
    },
    shouldGoToExternalUrl(externalUrlCondition) {
      return this[externalUrlCondition];
    },
    toggleNav () {
      this.sidenavToggle = !this.sidenavToggle
    },
    kebabString(str) {
      return str.replace(/\s/g , "-").toLowerCase()
    },
    signOutLink(linkTitle) {
      return linkTitle === PORTAL_NAV_TITLES.SIGN_OUT
    },
    switchBusinessModal(linkTitle) {
      return linkTitle === PORTAL_NAV_TITLES.SWITCH_BUSINESS && this.hasMultipleBorrowers
    },
    dashboardLink(linkTitle) {
      return linkTitle === PORTAL_NAV_TITLES.DASHBOARD
    },
    checkMobileLinks(linkTitle) {
      return this.mobileOnlyLinks.includes(linkTitle)
    },
    neitherSignOutOrModal(linkTitle) {
      return linkTitle != PORTAL_NAV_TITLES.SWITCH_BUSINESS && linkTitle != PORTAL_NAV_TITLES.SIGN_OUT
    },
    checkCurrentPath(linkUrl) {
      if (linkUrl === PORTAL_NAV_URLS.OFFERS) {
        return this.$route.path.includes(linkUrl)
      }
      return this.$route.path === linkUrl
    },
    logout () {
      this.loggingOut = true
      useRootStore(this.$pinia).logout( { $router: this.$router, path: this.$route && this.$route.path } )

      if (typeof window.logout !== 'undefined') {
        window.logout()
      }
    },
    businessSwitchToggle(value) {
      useBusinessSwitchStore(this.$pinia).businessSwitchModalToggle(value)
    },
  },
}
</script>

<style scoped lang="scss">


#side-nav {
  max-width: 300px;
  width: 100%;
  z-index: 100;
  @include media-breakpoint-up(lg) {
    background: #FFF;
  }
  @include media-breakpoint-down(lg) {
    position:fixed;
    top:0;
    right: 0;
    margin-top: 76px;
  }
  .nav-wrap {
    background: #FFF;
    @include media-breakpoint-up(lg) {
      top: 0;
      position: sticky;
      max-height: 100vh;
      overflow-y: auto;
    }
    @include media-breakpoint-down(lg) {
      box-shadow: 0px 5px 14px rgba(0, 0, 0, 0.2);
      border-radius: 14px;
      margin-top: 18px;
      position:relative;
      top:-25px;
    }
  ul {
    padding: 8px 0;
    margin: 0;
    list-style: none;
  }
  }
  li {
    a {
      padding: 8px 16px;
      border-radius: 18px;
      color: $black;
      font-size: 16px;
      white-space: nowrap;
      &:hover, &.current {
        text-decoration: none;
      }
      &:hover {
        color: $type-600;
        background: $disabled-background;
      }
      &.current {
        font-weight: bold;
        color: $black;
        background: $background-100;
        svg {
          color: $black;
        }
      }
      img {
        display:inline-block;
        margin-right: 10px;
        width: 18px;
      }
    }
  }

  .hamburger-menu {
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: -50px 10px 15px;
    width: 35px;
    cursor: pointer;
    padding: 10px;
    position: absolute;
    top: 0;
    right: 0;
    span {
      display: block;
      width: 15px;
      height: 2px;
      background-color: black;
      margin-bottom: 3px;
      border-radius: 2px;
    }
  }
  .fm-block {
    text-align:center;
    margin: 75px 20px 20px;
  }
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  // .mobile-menu-pointer {
  //   position: relative;
  //   width: 18px;
  //   height: 9px;
  //   margin-left: calc(100% - 3rem);
  //   top: -9px;
  //   background-image: url(https://www.lendio.com/wp-content/themes/lendio-standards/images/header/submenu-pointer-stroke.png);
  //   // Border cover
  //   &:after {
  //     content: '';
  //     background-color: #FFF;
  //     // Center
  //     position: absolute;
  //     left: 0;
  //     right: 0;
  //     margin: 0 auto;
  //     top: 100%; // Put at the bottom of the image above
  //     width: 14px; // 4 px less than the width of the image
  //     height: 2px; // 1 px bigger than height of the border you want to cover
  //   }
  //   @include media-breakpoint-up(xl) {
  //     margin: 0 auto;
  //   }
  // }
  .mobile-menu-pointer {
    &:before {
      position:absolute;
      top:-8px; right:10px;
      content: '';
      width: 0;
      height: 0;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      border-bottom: 10px solid #FFF;
      z-index:2;
    }
    &:after {
      /* This is the shadow triangle */
      position:absolute;
      top:-10
      px; right:8px;
      content: '';
      width: 0;
      height: 0;
      border-left: 12px solid transparent;
      border-right: 12px solid transparent;
      border-bottom: 12px solid rgba(0,0,0, 0.04);
      z-index:1;
    }
  }
}

</style>
